import { useState } from 'react'
import CustomModal from 'components/global/CustomModal'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import {
  convertToLocalExportTicketTime,
  convertToLocalRegistrationTime,
} from 'helper/utility'
import moment from 'moment'
import { CSVLink } from 'react-csv'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { Checkbox, Radio, RadioChangeEvent } from 'antd'
import { EventAttendanceExportResp } from 'types/event/eventAttendance'

const ExportAttendeesModal = ({
  data,
}: {
  data: EventAttendanceExportResp[]
}) => {
  const { attendanceModalOpen, setAttendanceModalOpen } = eventAttendanceStore(
    store => ({
      attendanceModalOpen: store.attendanceModalOpen,
      setAttendanceModalOpen: store.setAttendanceModalOpen,
    }),
  )

  const [fileFormat, setFileFormat] = useState('csv')
  const [selectedColumns, setSelectedColumns] = useState([
    'Key',
    'Profile Photo',
    'Name',
    'Age',
    'Date of Birth',
    'Type',
    'Gender',
    'Registration Date',
    'Registration Time',
    'Allergies',
    'Guardians',
    'Adults',
    'Tickets',
  ])
  const csvData = data.map(item => {
    const { registrationDate, registrationTime } =
      convertToLocalRegistrationTime(item?.registrationTime)
    return {
      key: item?._id,
      profilePhoto: item?.profilePhoto,
      name: item?.name,
      age: item?.age,
      dob: moment(item?.dob).format('MMM/DD/YYYY'),
      type: item?.type,
      gender: item?.gender,
      allergies: item?.allergies,
      guardians: item?.guardians,
      adults: item?.adults,
      tickets: convertToLocalExportTicketTime(item?.tickets),
      registrationDate,
      registrationTime,
    }
  })

  const headers = [
    { label: 'Profile Photo', key: 'profilePhoto' },
    { label: 'Name', key: 'name' },
    { label: 'Age', key: 'age' },
    { label: 'Date of Birth', key: 'dob' },
    { label: 'Type', key: 'type' },
    { label: 'Gender', key: 'gender' },
    { label: 'Registration Date', key: 'registrationDate' },
    { label: 'Registration Time', key: 'registrationTime' },
    { label: 'Allergies', key: 'allergies' },
    { label: 'Guardians', key: 'guardians' },
    { label: 'Adults', key: 'adults' },
    { label: 'Tickets', key: 'tickets' },
  ]

  const handleFormatChange = (e: RadioChangeEvent) => {
    setFileFormat(e.target.value)
  }

  const handleColumnChange = (checkedValue: boolean, label: string) => {
    setSelectedColumns(prev =>
      checkedValue ? [...prev, label] : prev.filter(col => col !== label),
    )
  }

  const handleExport = () => {
    const filteredHeaders = headers.filter(header =>
      selectedColumns.includes(header.label),
    )
    const filteredData = csvData.map(row => {
      const filteredRow = {}
      filteredHeaders.forEach(header => {
        filteredRow[header.key] = row[header.key]
      })
      return filteredRow
    })

    if (fileFormat === 'csv') {
      document.getElementById('csv-download-link').click()
    } else if (fileFormat === 'excel') {
      const ws = XLSX.utils.json_to_sheet(filteredData)
      const profilePhotoColIndex = filteredHeaders.findIndex(
        header => header.key === 'profilePhoto',
      )
      if (profilePhotoColIndex !== -1) {
        for (let i = 2; i <= filteredData.length + 1; i++) {
          const cellRef = XLSX.utils.encode_cell({
            r: i - 1,
            c: profilePhotoColIndex,
          })
          const cellValue = ws[cellRef]?.v
          if (cellValue) {
            ws[cellRef] = {
              f: `HYPERLINK("${cellValue}", "Profile Photo")`,
            }
          }
        }
      }

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, 'Event_Attendee.xlsx')
    } else if (fileFormat === 'pdf') {
      const doc = new jsPDF()
      const tableColumn = filteredHeaders.map(header => header.label)
      const tableRows = filteredData.map(row =>
        filteredHeaders.map(header => row[header.key]),
      )

      ;(doc as any).autoTable({
        head: [tableColumn],
        body: tableRows,
      })

      doc.save('Event_Attendee.pdf')
    }
    setAttendanceModalOpen(false)
  }

  return (
    <>
      <CustomModal
        openModal={attendanceModalOpen}
        closeModal={setAttendanceModalOpen}
        width={600}
        title="Export Attendees"
        footer={false}
      >
        <div className="formatText ">Format </div>
        <Radio.Group onChange={handleFormatChange} value={fileFormat}>
          <Radio value="csv">CSV</Radio>
          <Radio value="excel">Excel</Radio>
        </Radio.Group>
        <div className="radioText mt-15px">
          Please select columns to export{' '}
        </div>
        <div className="checkbox-group">
          {[
            'Profile Photo',
            'Name',
            'Age',
            'Date of Birth',
            'Type',
            'Gender',
            'Registration Date',
            'Registration Time',
            'Allergies',
            'Guardians',
            'Adults',
            'Tickets',
          ].map(label => (
            <div key={label}>
              <Checkbox
                checked={selectedColumns.includes(label)}
                onChange={e => handleColumnChange(e.target.checked, label)}
              >
                {label}
              </Checkbox>
            </div>
          ))}
        </div>
        <div className="flex justify-end w-full mt-10px">
          <div className="bg-grey radius-8 pointer mr-30px">
            <button
              className="prim-sm-btn"
              style={{ background: '#EBEDF1', color: 'black' }}
              onClick={() => setAttendanceModalOpen(false)}
            >
              Cancel
            </button>
          </div>
          <button className="prim-sm-btn" onClick={handleExport}>
            Export
          </button>
        </div>
        <CSVLink
          data={csvData}
          headers={headers.filter(header =>
            selectedColumns.includes(header.label),
          )}
          filename="Event_Attendee.csv"
          id="csv-download-link"
          style={{ display: 'none' }}
        />
      </CustomModal>
    </>
  )
}

export default ExportAttendeesModal
